import { memo, useState, useCallback, useMemo } from 'react';

import { setAtPath } from '@/utils';

import { Widget, getEditedPath } from '@/components/ui/widget';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from '@/shadcn/dialog';
import { Button } from '@/shadcn/button';
import type { TableRow, TableConfig } from '@/components/ui/table/table.type';
import { type WidgetType } from '@/components/ui/widget/widget.type';
import { type CoreViewType } from '@/components/ui/widget/views/views.type';

type EditableCell = {
  key: string;
  header: string;
  widget: WidgetType;
  editedPath: string;
};

type PatchData = Record<string, Partial<TableRow>>;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedRows: string[];
  columns: TableConfig['columns'];
  onSubmit: (
    patchData: PatchData,
    editablePaths: CoreViewType['path'][]
  ) => void;
};

export const BatchEditModal = memo(
  ({ open, onOpenChange, selectedRows, columns, onSubmit }: Props) => {
    const [modalPatch, setModalPatch] = useState<PatchData>({});

    const editableCells = useMemo(
      () =>
        columns.reduce((prevEditableCells, column) => {
          const { cell = {}, header, key } = column;
          const editedPath = getEditedPath(cell);
          if (editedPath !== null) {
            prevEditableCells.push({
              key,
              header,
              widget: cell as WidgetType,
              editedPath
            });
          }
          return prevEditableCells;
        }, [] as EditableCell[]),
      [columns]
    );

    const editableCellsUpdates = useMemo(
      () =>
        editableCells.map(cell => {
          const { header } = cell;

          return {
            patch: modalPatch,
            onChange: dataToChange => {
              const { path: modifiedDataPath, value: modifiedDataValue } =
                dataToChange;

              const newModalPatch = {
                ...modalPatch
              };

              // if value is empty ("") unset the patch at this path
              // ie batch won't update selected rows at this path
              setAtPath(
                newModalPatch,
                modifiedDataPath,
                modifiedDataValue === '' || modifiedDataValue === null
                  ? undefined
                  : modifiedDataValue
              );

              // update modal patch
              setModalPatch(newModalPatch);
            },
            editorOverrides: {
              header
            }
          };
        }),
      [editableCells, modalPatch]
    );

    const submitPatch = useCallback(() => {
      onSubmit(
        modalPatch,
        editableCells.map(cell => cell.editedPath)
      );
    }, [editableCells, modalPatch]);

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              Edit the{' '}
              {selectedRows.length > 1
                ? `${selectedRows.length} selected rows`
                : 'selected row'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col flex-nowrap text-sm">
            <div className="flex flex-col flex-nowrap gap-4">
              {editableCells.map((cell, cellIdx) => {
                const { key, widget } = cell;
                return (
                  <div key={key} className="relative">
                    <Widget
                      widget={widget}
                      data={{}}
                      update={editableCellsUpdates[cellIdx]}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter className="mt-2 flex w-full justify-between">
            <DialogClose asChild>
              <Button
                variant="outline"
                type="reset"
                aria-label="Cancel bulk edition"
                data-cy="cancel-batch-edit"
              >
                Cancel
              </Button>
            </DialogClose>

            <Button
              onClick={submitPatch}
              disabled={Object.keys(modalPatch).length === 0}
              data-cy="confirm-batch-edit"
            >
              Edit selected rows
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);
